import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CheckCircleIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const serviceData = {
  'kitchen-renovation': {
    title: 'Kitchen Renovation',
    hero: 'https://images.unsplash.com/photo-1556912167-f556f1f39fdf?w=1200&q=80',
    description: 'Transform your kitchen into a modern, functional space with premium finishes and smart design solutions tailored to Brisbane homes.',
    features: ['Custom cabinetry and storage solutions', 'Premium benchtops (stone, marble, engineered)', 'Modern appliances and fixtures', 'Smart lighting and electrical upgrades', 'Waterproofing and plumbing', 'Flooring installation'],
    process: [{
      step: 'Book Your Free Quote',
      description: "Choose a time that suits you with our online booking system, or contact us directly. We'll visit your Brisbane home, discuss your vision, measure the space, and provide a detailed, no-obligation quote with 3D renders of your new kitchen.",
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80'
    }, {
      step: 'Design & Planning',
      description: 'Our team works with you to finalize the design, select materials, and create a detailed project timeline. We coordinate with suppliers across Brisbane to ensure premium quality materials are ready for installation.',
      image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80'
    }, {
      step: 'We Get to Work',
      description: "Once you're ready, we'll schedule your renovation. Our licensed Brisbane tradespeople handle everything — demolition, plumbing, electrical, cabinetry installation, benchtop fitting, and tiling. You'll receive regular updates throughout the project.",
      image: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?w=800&q=80'
    }, {
      step: 'Job Complete — Enjoy Your New Kitchen',
      description: "After the finishing touches and thorough cleaning, we'll walk you through your brand-new kitchen to ensure you're 100% happy. Our aftercare support means you can relax knowing it's built to last with quality Brisbane craftsmanship.",
      image: 'https://images.unsplash.com/photo-1556912172-45b7abe8b7e1?w=800&q=80'
    }]
  },
  'bathroom-renovation': {
    title: 'Bathroom Renovation',
    hero: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1200&q=80',
    description: "Create a luxurious bathroom retreat with modern fixtures, premium finishes, and expert waterproofing for Brisbane's climate.",
    features: ['Full waterproofing to Australian standards', 'Premium tiles and fixtures', 'Custom vanities and storage', 'Modern shower and bath installations', 'Heated flooring options', 'Ventilation and moisture control'],
    process: [{
      step: 'Book Your Free Quote',
      description: "Schedule a consultation at your convenience. We'll visit your Brisbane property, discuss your bathroom vision, assess the space, and provide a comprehensive, obligation-free quote with design concepts.",
      image: 'https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?w=800&q=80'
    }, {
      step: 'We Get to Work',
      description: "Once approved, we'll begin your transformation. Our team handles everything—demolition, waterproofing to Queensland building codes, plumbing, tiling, fixture installation, and painting. Regular updates keep you informed throughout.",
      image: 'https://images.unsplash.com/photo-1620626011761-996317b8d101?w=800&q=80'
    }, {
      step: 'Job Complete — Enjoy Your New Bathroom',
      description: "After the finishing touches, we'll conduct a thorough walkthrough of your stunning new bathroom. We ensure everything meets our high standards and your expectations. Our aftercare support guarantees long-lasting quality.",
      image: 'https://images.unsplash.com/photo-1564540583246-934409427776?w=800&q=80'
    }]
  },
  'full-house-renovation': {
    title: 'Full House Renovation',
    hero: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1200&q=80',
    description: "Complete home transformation with premium finishes throughout, perfect for Brisbane's Queenslander homes and modern properties.",
    features: ['Structural modifications and extensions', 'Complete electrical and plumbing upgrades', 'Kitchen and bathroom renovations', 'New flooring throughout', 'Interior and exterior painting', 'Smart home integration'],
    process: [{
      step: 'Initial Consultation & Quote',
      description: "We'll visit your Brisbane home for a comprehensive assessment. Our team will discuss your vision, evaluate structural requirements, and provide a detailed quote with project timeline and 3D visualizations.",
      image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=80'
    }, {
      step: 'Design & Approvals',
      description: 'Our designers create detailed plans for your renovation. We handle all Brisbane City Council approvals and building permits, ensuring your project meets Queensland building codes and regulations.',
      image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80'
    }, {
      step: 'Construction Phase',
      description: 'Our experienced Brisbane team manages every aspect — from structural work to finishing touches. We coordinate all trades, maintain the site, and provide weekly progress updates with photos.',
      image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80'
    }, {
      step: 'Final Inspection & Handover',
      description: "We conduct a thorough inspection with you, addressing any final details. You'll receive all warranties, maintenance guides, and our ongoing support to ensure your renovated home exceeds expectations.",
      image: 'https://images.unsplash.com/photo-1600210492493-0946911123ea?w=800&q=80'
    }]
  },
  'flooring-painting': {
    title: 'Flooring & Painting',
    hero: 'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=1200&q=80',
    description: 'Premium flooring installation and professional painting services to refresh and modernize your Brisbane home.',
    features: ['Hardwood, engineered, and laminate flooring', 'Tile and vinyl plank installation', 'Interior and exterior painting', 'Feature walls and decorative finishes', 'Floor preparation and leveling', 'Color consultation and design'],
    process: [{
      step: 'Free Quote & Color Consultation',
      description: "Book a consultation where we'll assess your space, discuss flooring and paint options suitable for Brisbane's climate, and provide color recommendations. You'll receive a detailed quote with material samples.",
      image: 'https://images.unsplash.com/photo-1589939705384-5185137a7f0f?w=800&q=80'
    }, {
      step: 'Preparation & Installation',
      description: 'Our team prepares surfaces, protects your furniture, and begins installation. For flooring, we ensure proper subfloor preparation. For painting, we prep walls, apply primer, and use premium paints designed for Queensland conditions.',
      image: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800&q=80'
    }, {
      step: 'Quality Check & Completion',
      description: 'We conduct a thorough inspection to ensure flawless results. Your new floors and freshly painted walls will transform your space. We provide care instructions and warranty information for your peace of mind.',
      image: 'https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=800&q=80'
    }]
  },
  'plumbing-electrical': {
    title: 'Plumbing & Electrical',
    hero: 'https://uploadthingy.s3.us-west-1.amazonaws.com/udKXTpCQCLPDCr4RZZ47z1/What_Do_Electrical_Engineers_Do.jpg',
    description: 'Licensed plumbing and electrical services for renovations, upgrades, and new installations across Brisbane.',
    features: ['Licensed Brisbane electricians and plumbers', 'Switchboard and wiring upgrades', 'Hot water system installation', 'Bathroom and kitchen plumbing', 'Smart home electrical integration', 'Emergency repairs and maintenance'],
    process: [{
      step: 'Assessment & Quote',
      description: "Our licensed Brisbane tradespeople will assess your plumbing and electrical needs, ensuring compliance with Queensland regulations. We'll provide a detailed quote with recommendations for upgrades and improvements.",
      image: 'https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=800&q=80'
    }, {
      step: 'Professional Installation',
      description: 'Our team carries out all work to Australian Standards, with proper permits and inspections. We handle everything from simple repairs to complete rewiring or replumbing, ensuring safety and reliability.',
      image: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=800&q=80'
    }, {
      step: 'Testing & Certification',
      description: "All work is thoroughly tested and certified. You'll receive compliance certificates for electrical work and warranties on all installations. Our team ensures everything operates perfectly before completion.",
      image: 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=800&q=80'
    }]
  },
  'roofing-renovation': {
    title: 'Roofing Renovation',
    hero: 'https://uploadthingy.s3.us-west-1.amazonaws.com/aPNuuyzSTsP9Z7jWz9hKeN/roofing_brisbrene.jpg',
    description: "Durable roofing solutions designed for Brisbane's climate, from repairs to complete roof replacements.",
    features: ['Roof repairs and restoration', 'Complete roof replacement', 'Colorbond and tile roofing', 'Gutter and downpipe installation', 'Roof insulation and ventilation', 'Storm damage repairs'],
    process: [{
      step: 'Roof Inspection & Quote',
      description: "We'll conduct a comprehensive roof inspection of your Brisbane property, identifying any issues and recommending solutions. You'll receive a detailed quote with photos and material options suitable for Queensland weather.",
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3biMGvKXwzijxoweMjCBUC/roofing_brisbrene.jpg'
    }, {
      step: 'Professional Installation',
      description: "Our licensed roofing team works efficiently to minimize disruption. We handle all aspects — from removing old materials to installing new roofing, ensuring proper waterproofing and ventilation for Brisbane's climate.",
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/izpiw5fVT9ZWHAd3ex8Bhc/Roofing_Image.jpg'
    }, {
      step: 'Final Inspection & Warranty',
      description: 'We conduct a thorough inspection to ensure quality workmanship and proper drainage. Your new roof comes with comprehensive warranties, giving you peace of mind for years to come.',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/guAXXuygtouM5Qmcsg7oaK/Roofing_2023.webp'
    }]
  },
  'new-builds': {
    title: 'New Builds',
    hero: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80',
    description: 'Custom new home construction with modern design, premium materials, and smart home integration for Brisbane living.',
    features: ['Custom home design', 'Premium construction materials', 'Smart home technology', 'Energy-efficient solutions', 'Luxury finishes throughout', 'Project management from start to finish'],
    process: [{
      step: 'Design Consultation',
      description: "Meet with our design team to discuss your vision, budget, and timeline. We'll create custom plans for your Brisbane block, considering orientation, views, and local building regulations.",
      image: 'https://images.unsplash.com/photo-1600607687644-c7171b42498f?w=800&q=80'
    }, {
      step: 'Approvals & Documentation',
      description: 'We handle all Brisbane City Council approvals, building permits, and engineering requirements. Our team ensures your project complies with Queensland building codes and local planning schemes.',
      image: 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800&q=80'
    }, {
      step: 'Construction',
      description: 'Our experienced team manages every phase of construction — from foundation to final finishes. We coordinate all trades, maintain quality control, and provide regular updates throughout the build process.',
      image: 'https://images.unsplash.com/photo-1600585154363-67eb9e2e2099?w=800&q=80'
    }, {
      step: 'Handover & Settlement',
      description: "We conduct a comprehensive walkthrough of your new home, ensuring every detail meets our standards. You'll receive all warranties, manuals, and ongoing support as you settle into your dream home.",
      image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=80'
    }]
  }
};
export function ServiceDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const service = slug ? serviceData[slug as keyof typeof serviceData] : null;
  if (!service) {
    return <div className="w-full min-h-screen bg-[#0a1628] flex items-center justify-center" data-id="element-288">
        <div className="text-center" data-id="element-289">
          <h1 className="text-4xl font-bold text-white mb-4" data-id="element-290">
            Service Not Found
          </h1>
          <button onClick={() => navigate('/')} className="text-[#d4af37] hover:text-white transition-colors" data-id="element-291">
            Return Home
          </button>
        </div>
      </div>;
  }
  return <div className="w-full min-h-screen bg-[#0a1628]" data-id="element-292">
      <Header data-id="element-293" />
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden pt-20" data-id="element-294">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: `linear-gradient(180deg, rgba(10, 22, 40, 0.7) 0%, rgba(27, 43, 69, 0.8) 100%), url('${service.hero}')`
      }} data-id="element-295" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-296">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-[#d4af37] hover:text-white transition-colors mb-6" data-id="element-297">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-298" />
            Back to Services
          </button>
          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4" data-id="element-299">
            {service.title}
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-lg sm:text-xl text-gray-200 max-w-3xl mx-auto" data-id="element-300">
            {service.description}
          </motion.p>
        </div>
      </section>
      {/* Features Section */}
      <section className="py-16 bg-[#0f0f0f]" data-id="element-301">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-302">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8 text-center" data-id="element-303">
            What's <span className="text-[#d4af37]" data-id="element-304">Included</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-305">
            {service.features.map((feature, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="flex items-start gap-3 bg-[#1c1c1c] p-6 rounded-lg" data-id="element-306">
                <CheckCircleIcon className="w-6 h-6 text-[#d4af37] flex-shrink-0 mt-1" data-id="element-307" />
                <p className="text-gray-300" data-id="element-308">{feature}</p>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* Process Section */}
      <section className="py-16 bg-[#0a1628]" data-id="element-309">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-310">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 text-center" data-id="element-311">
            From Quote to <span className="text-[#d4af37]" data-id="element-312">Completion</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto" data-id="element-313">
            Our streamlined process ensures your project runs smoothly from
            start to finish
          </p>
          <div className="space-y-16" data-id="element-314">
            {service.process.map((step, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className={`grid md:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`} data-id="element-315">
                <div className={index % 2 === 1 ? 'md:order-2' : ''} data-id="element-316">
                  <div className="flex items-center gap-4 mb-4" data-id="element-317">
                    <div className="w-12 h-12 bg-[#d4af37] rounded-full flex items-center justify-center text-[#0a1628] font-bold text-xl flex-shrink-0" data-id="element-318">
                      {index + 1}
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-white" data-id="element-319">
                      {step.step}
                    </h3>
                  </div>
                  <p className="text-gray-300 text-lg leading-relaxed" data-id="element-320">
                    {step.description}
                  </p>
                </div>
                <div className={index % 2 === 1 ? 'md:order-1' : ''} data-id="element-321">
                  <img src={step.image} alt={step.step} className="w-full h-[300px] object-cover rounded-lg shadow-2xl" data-id="element-322" />
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-[#0f0f0f]" data-id="element-323">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-324">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-id="element-325">
            Ready to Get <span className="text-[#d4af37]" data-id="element-326">Started?</span>
          </h2>
          <p className="text-gray-400 text-lg mb-8" data-id="element-327">
            Contact us today for a free consultation and quote
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-328">
            <button onClick={() => {
            navigate('/');
            setTimeout(() => {
              const contactSection = document.getElementById('contact');
              contactSection?.scrollIntoView({
                behavior: 'smooth'
              });
            }, 100);
          }} className="bg-[#d4af37] text-[#0a1628] px-8 py-4 rounded-full font-semibold hover:bg-[#4b4bff] hover:text-white transition-all duration-300 transform hover:scale-105" data-id="element-329">
              Get a Free Quote
            </button>
            <a href="tel:+61721004589" className="bg-transparent text-white px-8 py-4 rounded-full font-semibold border border-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a1628] transition-all duration-300" data-id="element-330">
              Call Us: +61 7 2100 4589
            </a>
          </div>
        </div>
      </section>
      <Footer data-id="element-331" />
    </div>;
}